import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Star, ChevronDown, Search, X } from 'lucide-react';
import { toolsRegistry } from '../config/tools.registry';
import { CATEGORY_CONFIG } from '../config/categories';
import { useToolFavorites } from '../hooks/useToolFavorites';
import { LoadingSpinner } from '../../ui';
import { useKeyboardContext, serializeKeyCombo } from '../../../contexts/KeyboardContext';
import { useUnifiedToolPermissions } from '../../../hooks/useUnifiedPermissions';
import { usePermissions } from '../../../contexts/PermissionContext';
import { sendToTerminal } from '../../../lib/terminalLogger';
import { LogCategory } from '../../../config/logging';
import type { ToolDefinition } from '../types/tools';

interface ToolsGridProps {
  isReady: boolean;
  sendingId: string | null;
  oneAgruparRunning: boolean;
  onToolClick: (id: string) => void;
  busyToolIds?: Set<string>;
  shortcutsActive: boolean;
  layersUnlocked?: boolean; // Novo prop para estado visual
  layerScope?: 'active' | 'document';
  onSetLayerScope?: (scope: 'active' | 'document') => void;
}

// IDs das ferramentas que devem ser ocultadas (hardcoded - em desenvolvimento)
const HIDDEN_TOOLS = ['onebitmap', 'collectmetadata', 'onecolorcheck'];

// Função helper para obter o actionId principal de uma ferramenta
function getToolActionId(tool: typeof toolsRegistry[0]): string | null {
  if ((tool as any).opensModalId) return 'abrir-modal';
  if ((tool as any).actionId) return String((tool as any).actionId);
  return null;
}

export function ToolsGrid({ isReady, sendingId, oneAgruparRunning, onToolClick, busyToolIds, shortcutsActive, layersUnlocked, layerScope, onSetLayerScope }: ToolsGridProps) {
  const { state: shortcutsState } = useKeyboardContext();
  const { favorites, toggleFavorite, isFavorite } = useToolFavorites();
  const { permissionsLoading } = usePermissions();

  // Estado de categorias recolhidas (persistido no localStorage)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => {
    try {
      const saved = window.localStorage.getItem('cdr:tools:collapsed');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleCollapse = useCallback((key: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try { window.localStorage.setItem('cdr:tools:collapsed', JSON.stringify([...next])); } catch {}
      return next;
    });
  }, []);

  // Campo de busca
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ✅ DEBUG: Log apenas quando estado muda de vazio para preenchido (reduzir logs)
  const prevShortcutsCountRef = useRef(0);
  useEffect(() => {
    const shortcutsCount = Object.keys(shortcutsState || {}).length;
    const totalBindings = shortcutsState ? Object.values(shortcutsState).reduce(
      (sum, actions) => sum + Object.keys(actions).length,
      0
    ) : 0;

    // ✅ Log apenas quando estado muda significativamente (vazio → preenchido ou vice-versa)
    if (prevShortcutsCountRef.current === 0 && shortcutsCount > 0) {
      const allToolIds = shortcutsState ? Object.keys(shortcutsState) : [];
      sendToTerminal('✅ [TOOLS_GRID] Atalhos carregados no contexto', {
        tools: shortcutsCount,
        bindings: totalBindings,
        toolIds: allToolIds
      }, LogCategory.API);
    } else if (prevShortcutsCountRef.current > 0 && shortcutsCount === 0) {
      sendToTerminal('⚠️ [TOOLS_GRID] Estado de atalhos foi limpo', {}, LogCategory.API);
    }

    prevShortcutsCountRef.current = shortcutsCount;
  }, [shortcutsState]);

  // Obter IDs de todas as ferramentas para verificar permissões
  const allToolIds = useMemo(() => toolsRegistry.map(t => t.id), []);
  const toolPermissions = useUnifiedToolPermissions(allToolIds);

  const isToolBusy = (toolId: string) => {
    return (sendingId === toolId) || (toolId === 'oneagrupar' && oneAgruparRunning) || (busyToolIds?.has(toolId) ?? false);
  };

  const isToolDisabled = (tool: typeof toolsRegistry[0]) => {
    return !isReady || !!tool.disabled || isToolBusy(tool.id);
  };

  // Função para obter o atalho de uma ferramenta
  const getToolShortcut = (tool: typeof toolsRegistry[0]): string | null => {
    const actionId = getToolActionId(tool);
    if (!actionId) {
      return null;
    }

    const toolShortcuts = shortcutsState?.[tool.id];
    if (!toolShortcuts) {
      return null;
    }

    const keyCombo = toolShortcuts[actionId];
    if (!keyCombo) {
      return null;
    }

    return serializeKeyCombo(keyCombo);
  };

  // Filtrar ferramentas visiveis e agrupar por categoria
  const visibleTools = useMemo(() => {
    return toolsRegistry.filter(tool => {
      if (HIDDEN_TOOLS.includes(tool.id)) return false;
      if (tool.id === 'gerenciar-camadas') return true;
      if (tool.requiresPermission === false) return true;
      if (tool.disabled) return false;
      return toolPermissions[tool.id]?.allowed ?? true;
    });
  }, [toolPermissions]);

  // Filtrar por busca
  const searchFiltered = useMemo(() => {
    if (!searchQuery.trim()) return visibleTools;
    const q = searchQuery.toLowerCase().trim();
    return visibleTools.filter(t => t.name.toLowerCase().includes(q));
  }, [visibleTools, searchQuery]);

  // Agrupar ferramentas por categoria (com favoritos no topo)
  const groupedTools = useMemo(() => {
    const groups: { key: string; label: string; tools: ToolDefinition[] }[] = [];

    // Secao de favoritos no topo (so se nao esta buscando)
    if (!searchQuery.trim() && favorites.length > 0) {
      const favTools = favorites
        .map(id => searchFiltered.find(t => t.id === id))
        .filter((t): t is ToolDefinition => !!t);
      if (favTools.length > 0) {
        groups.push({ key: '_favorites', label: 'Favoritos', tools: favTools });
      }
    }

    // Categorias normais
    for (const cat of CATEGORY_CONFIG) {
      const tools = searchFiltered.filter(t => t.category === cat.key);
      if (tools.length > 0) {
        groups.push({ key: cat.key, label: cat.label, tools });
      }
    }

    // Ferramentas sem categoria (fallback)
    const uncategorized = searchFiltered.filter(t => !t.category);
    if (uncategorized.length > 0) {
      groups.push({ key: 'utility', label: 'Outros', tools: uncategorized });
    }

    return groups;
  }, [searchFiltered, favorites, searchQuery]);

  // Renderiza um card de ferramenta individual
  const renderToolCard = (tool: ToolDefinition, groupKey: string) => {
    const disabled = isToolDisabled(tool);
    const busy = isToolBusy(tool.id);
    const isLoading = busy && !tool.disabled;
    const shortcut = getToolShortcut(tool);
    const isToggled = tool.id === 'gerenciar-camadas' && layersUnlocked;

    const baseButtonStyles = `
      relative flex flex-col items-center
      rounded-lg overflow-hidden
      transition-all duration-200
      w-full
    `;

    const stateStyles = !isReady
      ? 'bg-navy-800/30 border border-navy-700/30 cursor-not-allowed'
      : tool.disabled
        ? 'bg-navy-800/20 border border-navy-700/20 cursor-not-allowed opacity-40'
        : isToggled
          ? 'bg-green-900/30 border border-green-500/50 hover:bg-green-800/40 hover:border-green-400 cursor-pointer shadow-[0_0_10px_rgba(34,197,94,0.15)]'
          : 'bg-navy-800/40 border border-navy-700/50 hover:bg-navy-700/50 hover:border-navy-600 cursor-pointer';

    const busyStyles = busy ? 'opacity-60 cursor-wait' : '';

    // Renderizacao especial para Gerenciar Camadas
    if (tool.id === 'gerenciar-camadas') {
      return (
        <div
          key={`${groupKey}:${tool.id}`}
          className={`${baseButtonStyles} ${stateStyles} ${busyStyles} !p-0`}
        >
          <div
            className="flex flex-col items-center justify-center p-2 pt-3 flex-1 w-full hover:bg-white/5 active:bg-white/10"
            onClick={() => { !disabled && onToolClick(tool.id); }}
          >
            {isLoading && (
              <div className="mb-2">
                <LoadingSpinner size="sm" color={tool.disabled ? 'red' : 'blue'} className="w-4 h-4" />
              </div>
            )}
            <span className={`text-xs font-medium ${tool.disabled ? 'text-gray-500' : 'text-gray-300'} text-center leading-tight mb-2`}>
              {tool.name}
            </span>
            {shortcut && (
              <div
                className={`mt-1 px-1.5 py-0.5 rounded bg-black/20 border border-white/10 text-[9px] font-mono font-bold tracking-tight ${isToggled ? 'text-green-200' : 'text-gray-400'}`}
                title={`Atalho: ${shortcut}`}
              >
                {shortcut}
              </div>
            )}
          </div>
          <div className="w-full grid grid-cols-2 text-[10px] divide-x divide-navy-700/50 bg-navy-900/30">
            <button
              onClick={(e) => { e.stopPropagation(); if (!disabled && onSetLayerScope) onSetLayerScope('active'); }}
              className={`py-1.5 px-0.5 text-center font-medium transition-colors hover:bg-white/5 ${layerScope === 'active' ? (isToggled ? 'text-green-300 bg-green-900/20' : 'text-blue-300 bg-blue-900/20') : 'text-gray-500'}`}
            >
              Page
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); if (!disabled && onSetLayerScope) onSetLayerScope('document'); }}
              className={`py-1.5 px-0.5 text-center font-medium transition-colors hover:bg-white/5 ${layerScope === 'document' ? (isToggled ? 'text-green-300 bg-green-900/20' : 'text-blue-300 bg-blue-900/20') : 'text-gray-500'}`}
            >
              All
            </button>
          </div>
        </div>
      );
    }

    // Renderizacao padrao
    const fav = isFavorite(tool.id);

    return (
      <button
        key={`${groupKey}:${tool.id}`}
        onClick={() => { !disabled && onToolClick(tool.id); }}
        disabled={disabled}
        title={!isReady ? 'Sessão não carregada' : tool.disabled ? 'Desabilitado' : tool.name}
        className={`${baseButtonStyles} ${stateStyles} ${busyStyles}`}
      >
        {/* Estrela de favorito */}
        <div
          className="absolute top-1 right-1 z-10 p-0.5 rounded hover:bg-white/10 transition-colors"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); toggleFavorite(tool.id); }}
          title={fav ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <Star className={`w-3 h-3 transition-colors ${fav ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600 hover:text-gray-400'}`} />
        </div>

        <div className="flex flex-col items-center justify-center p-2 flex-1 w-full">
          <div className={`w-8 h-8 ${tool.disabled ? 'bg-navy-700/30' : isToggled ? 'bg-green-800/50' : 'bg-navy-700/50'} rounded-lg flex items-center justify-center mb-1.5 transition-colors duration-200`}>
            {isLoading ? (
              <LoadingSpinner size="sm" color={tool.disabled ? 'red' : 'blue'} className="w-4 h-4" />
            ) : (
              <tool.icon className={`${tool.disabled ? 'text-gray-500' : isToggled ? 'text-green-400' : 'text-blue-400'} w-4 h-4`} />
            )}
          </div>
          <span className={`text-xs font-medium ${tool.disabled ? 'text-gray-500' : 'text-gray-300'} text-center leading-tight`}>
            {tool.name}
          </span>
        </div>
        {shortcut && (
          <div
            className={`w-full py-1 px-1 ${shortcutsActive ? 'bg-blue-600/50 border-t border-blue-500/60' : 'bg-gray-700/40 border-t border-gray-600/40 cursor-help'} flex items-center justify-center`}
            title={!shortcutsActive ? 'Atalho inativo. Reabra a janela "Ferramentas" para ativar os atalhos.' : undefined}
          >
            <span className={`text-xs font-bold font-mono tracking-wide ${shortcutsActive ? 'text-blue-100' : 'text-gray-400'}`}>
              {shortcut}
            </span>
          </div>
        )}
      </button>
    );
  };

  const isSearching = searchQuery.trim().length > 0;

  // Aguardar permissoes antes de renderizar ferramentas
  if (permissionsLoading) {
    return (
      <div className="w-full flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="w-full min-h-0 space-y-2">
      {/* Campo de busca */}
      <div className="relative px-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
        <input
          ref={searchInputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar ferramenta..."
          className="w-full bg-navy-900/60 border border-navy-700/50 rounded-md pl-8 pr-7 py-1.5 text-xs text-gray-300 placeholder-gray-600 focus:border-blue-500/50 focus:outline-none transition-colors"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Categorias */}
      {groupedTools.map((group) => {
        const isCollapsed = !isSearching && collapsedCategories.has(group.key);
        const isFav = group.key === '_favorites';

        return (
          <div key={group.key}>
            {/* Header da categoria (clicavel para recolher/expandir) */}
            <button
              type="button"
              onClick={() => !isSearching && toggleCollapse(group.key)}
              className={`flex items-center gap-2 px-1 py-1.5 w-full group select-none rounded transition-colors ${isSearching ? 'cursor-default' : 'cursor-pointer hover:bg-navy-800/30'}`}
            >
              {!isSearching && (
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isFav ? 'text-yellow-400' : 'text-gray-500'} ${isCollapsed ? '-rotate-90' : ''}`} />
              )}
              {isFav && <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />}
              <span className={`text-[11px] font-semibold uppercase tracking-wider ${isFav ? 'text-yellow-400' : 'text-gray-500'} ${!isSearching ? 'group-hover:text-gray-300' : ''} transition-colors`}>
                {group.label}
                <span className="ml-1 text-[10px] font-normal opacity-60">({group.tools.length})</span>
              </span>
              <div className={`flex-1 h-px ${isFav ? 'bg-yellow-700/30' : 'bg-navy-700/50'}`} />
            </button>
            {/* Grid de ferramentas da categoria */}
            {!isCollapsed && (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 auto-rows-max mt-1">
                {group.tools.map(tool => renderToolCard(tool, group.key))}
              </div>
            )}
          </div>
        );
      })}

      {/* Nenhum resultado */}
      {groupedTools.length === 0 && isSearching && (
        <div className="text-center py-6 text-gray-500 text-xs">
          Nenhuma ferramenta encontrada para "{searchQuery}"
        </div>
      )}
    </div>
  );
}
